import { useEffect, useState } from 'react'
import { Link, useLocation } from 'wouter'
import { BackdropOrbs } from '../components/BackdropOrbs'
import { SiteFooter } from '../components/SiteFooter'
import { CharacterImage } from '../components/CharacterImage'
import { Markdown } from '../components/Markdown'
import { RichMarkdown } from '../components/RichMarkdown'
import { ApiError } from '../lib/api'
import { fetchCharacter, linkLabel, requestManageKey, type PublicCharacter } from '../lib/characters'

/** A single community character: portrait on the left, identity + description
 *  + lorebook entry stacked on the right. */

export function CharacterDetail({ id }: { id: string }) {
  const [, setLocation] = useLocation()
  const [char, setChar] = useState<PublicCharacter | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setChar(null)
    setError(null)
    fetchCharacter(id)
      .then((res) => alive && setChar(res.character))
      .catch((err) => {
        if (!alive) return
        setError(
          err instanceof ApiError && err.status === 404
            ? 'No character filed under this id. It may have been deleted.'
            : 'Could not reach the archive. Try again in a moment.',
        )
      })
    return () => {
      alive = false
    }
  }, [id])

  const onEdit = async () => {
    if (!char) return
    const key = await requestManageKey(char.id)
    if (key) setLocation(`/characters/${char.id}/edit`)
  }

  return (
    <div className="lorebook-root flex min-h-screen flex-col">
      <BackdropOrbs />
      <header className="sticky top-0 z-30 border-b border-edge bg-void/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-4 py-3 lg:px-6">
          <div className="flex items-center gap-3">
            <Link
              href="/characters"
              className="text-sm text-slate-300 transition hover:text-signal"
            >
              ← Characters
            </Link>
            {char && (
              <>
                <span className="text-slate-700">/</span>
                <span className="truncate font-display text-sm text-white">{char.title}</span>
              </>
            )}
          </div>
          {char && (
            <button
              type="button"
              onClick={onEdit}
              className="rounded-full border border-signal/40 px-4 py-1.5 text-sm text-signal transition hover:border-signal/60 hover:bg-signal/10 hover:text-white"
            >
              Edit
            </button>
          )}
        </div>
      </header>

      <div className="mx-auto w-full max-w-6xl flex-1 px-4 py-10 lg:px-6">
        {error && (
          <div className="rounded-lg border border-sos/30 bg-sos/5 p-8 text-center text-sm text-slate-300">
            {error}
            <div className="mt-4">
              <Link href="/characters" className="text-signal hover:underline">
                Back to the gallery
              </Link>
            </div>
          </div>
        )}

        {!error && char === null && (
          <div className="animate-pulse rounded-lg border border-edge bg-void-2/60 p-8">
            <div className="h-6 w-1/3 rounded bg-void" />
            <div className="mt-4 h-3 w-2/3 rounded bg-void" />
          </div>
        )}

        {!error && char !== null && (
          <div className="grid gap-10 lg:grid-cols-[340px_1fr]">
            {/* left: portrait + outbound links */}
            <aside className="lg:sticky lg:top-24 lg:self-start">
              <div className="overflow-hidden rounded-lg border border-edge bg-void-2/60">
                <CharacterImage src={char.imageUrl} alt={char.title} />
                {char.links.map((url, i) => (
                  <a
                    key={url}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className={
                      'block bg-void-2 px-4 py-3 text-center text-xs font-medium uppercase tracking-[0.18em] text-slate-400 transition hover:bg-signal/10 hover:text-signal' +
                      (i < char.links.length - 1 ? ' border-b border-edge' : '')
                    }
                  >
                    {linkLabel(url)}
                  </a>
                ))}
              </div>
            </aside>

            {/* right: identity, description, lorebook entry */}
            <div className="min-w-0">
              <h1 className="font-display text-4xl font-semibold leading-tight text-white">
                {char.title}
              </h1>
              <p className="mt-2 text-base leading-relaxed text-slate-400">{char.tagline}</p>
              <div className="mt-3 text-[11px] uppercase tracking-[0.2em] text-slate-500">
                {char.creator ? `by ${char.creator}` : 'anonymous'} · filed{' '}
                {new Date(char.createdAt).toLocaleDateString()}
              </div>

              <div className="mt-8 space-y-10">
                {char.description && (
                  <section>
                    <div className="mb-3 text-[11px] uppercase tracking-[0.35em] text-signal/70">
                      Description
                    </div>
                    <div className="md text-slate-300">
                      <RichMarkdown>{char.description}</RichMarkdown>
                    </div>
                  </section>
                )}

                <section>
                  <div className="mb-3 flex items-baseline justify-between gap-4">
                    <div className="text-[11px] uppercase tracking-[0.35em] text-signal/70">
                      Lorebook entry
                    </div>
                    {typeof char.lorebookTokens === 'number' && (
                      <div className="text-[11px] text-slate-500">
                        {char.lorebookTokens} tokens
                      </div>
                    )}
                  </div>
                  {char.lorebook ? (
                    <div className="md rounded-lg border border-edge bg-void-2/40 p-6">
                      <Markdown plain>{char.lorebook}</Markdown>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-edge border-dashed bg-void-2/30 p-6 text-sm text-slate-500">
                      No lorebook entry was filed for this character yet. WIP.
                    </div>
                  )}
                </section>
              </div>
            </div>
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  )
}
